import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import VisibilityIcon from '@mui/icons-material/Visibility';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { ToggleButtonGroup, ToggleButton, Grid } from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { getAllInformes, getPacienteInformeById, getUserById } from '../api/otrosApi';
const TESIS_TORRES_ID = 3;

const SORT_OPTIONS = [
    { value: 'fecha_desc', label: 'Más reciente', compare: (a, b) => b.fechaRaw - a.fechaRaw },
    { value: 'pacienteId_asc', label: 'ID', compare: (a, b) => a.pacienteId - b.pacienteId },
    { value: 'paciente_asc', label: 'Nombre', compare: (a, b) => (a.paciente ?? '').localeCompare(b.paciente ?? '') },
];

const columns = [
    { id: 'tipo', label: 'TIPO', minWidth: 170 },
    { id: 'profesional', label: 'PROFESIONAL', minWidth: 100 },
    { id: 'paciente', label: 'PACIENTE', minWidth: 170, align: 'right' },
    { id: 'fecha', label: 'FECHA (dd-mm-yyyy)', minWidth: 170, align: 'right' },
    { id: 'actions', label: '', minWidth: 50, align: 'right' },
];

function OtrosTable() {
    const navigate = useNavigate();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [reports, setReports] = useState([]);
    const [filterPaciente, setFilterPaciente] = useState('');
    const [filterProfesional, setFilterProfesional] = useState('');
    const [sortKey, setSortKey] = useState('fecha_desc');

    useEffect(() => {
        getAllInformes()
            .then(data => {
                formatData(data.filter(informe => informe.otrosId !== null));
            })
            .catch(error => console.error('Error fetching data:', error));
    }, []);

    const formatData = (data) => {
        const formattedData = data.map(informe => {
            const idTipo = informe.otrosId;
            const tipo = idTipo === TESIS_TORRES_ID ? 'TESIS TORRES' : 'CUESTIONARIO';
            const d = new Date(informe.createdAt);
            const day = String(d.getDate()).padStart(2, '0');
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const year = d.getFullYear();
            return {
                id: informe.id,
                idTipo,
                tipo,
                pacienteId: informe.pacienteId,
                userId: informe.userId,
                fechaRaw: d.getTime(),
                fecha: `${day}-${month}-${year}`,
            };
        });

        Promise.all(formattedData.map(r => getPacienteInformeById(r.pacienteId)))
            .then(pacientes => {
                formattedData.forEach((r, i) => {
                    r.paciente = `${pacientes[i].firstname} ${pacientes[i].lastname}`;
                });
                return Promise.all(formattedData.map(r => getUserById(r.userId)));
            })
            .then(users => {
                formattedData.forEach((r, i) => {
                    r.profesional = `${users[i].firstname} ${users[i].lastname}`;
                });
                setReports(formattedData);
            })
            .catch(error => console.error('Error fetching data:', error));
    };

    const handleResetFilters = () => {
        setFilterPaciente('');
        setFilterProfesional('');
        setSortKey('fecha_desc');
    };

    const sortFn = SORT_OPTIONS.find(o => o.value === sortKey)?.compare ?? SORT_OPTIONS[0].compare;

    const filteredReports = reports
        .filter(r => filterPaciente === '' || (r.paciente ?? '').toLowerCase().includes(filterPaciente.toLowerCase()))
        .filter(r => filterProfesional === '' || (r.profesional ?? '').toLowerCase().includes(filterProfesional.toLowerCase()))
        .sort(sortFn);

    return (
        <>
            <Typography variant="h6" component="h2" gutterBottom color='#0D6AEE'>
                Informes de Cuestionarios
            </Typography>

            <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Grid item>
                    <TextField
                        name="paciente"
                        label="Filtrar por Paciente"
                        value={filterPaciente}
                        onChange={e => setFilterPaciente(e.target.value)}
                        size="small"
                    />
                </Grid>
                <Grid item>
                    <TextField
                        name="profesional"
                        label="Filtrar por Profesional"
                        value={filterProfesional}
                        onChange={e => setFilterProfesional(e.target.value)}
                        size="small"
                    />
                </Grid>
                <Grid item>
                    <ToggleButtonGroup
                        value={sortKey}
                        exclusive
                        size="small"
                        onChange={(_, val) => { if (val) setSortKey(val); }}
                    >
                        {SORT_OPTIONS.map(o => (
                            <ToggleButton key={o.value} value={o.value}>{o.label}</ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </Grid>
                <Grid item>
                    <Button variant="contained" color="secondary" onClick={handleResetFilters} startIcon={<RestartAltIcon />}>
                        Resetear
                    </Button>
                </Grid>
            </Grid>

            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                <TableContainer sx={{ maxHeight: 440 }}>
                    <Table stickyHeader aria-label="sticky table">
                        <TableHead>
                            <TableRow>
                                {columns.map(column => (
                                    <TableCell
                                        key={column.id}
                                        align={column.align}
                                        style={{ minWidth: column.minWidth, textDecoration: 'underline', color: '#0D6AEE' }}
                                    >
                                        {column.label}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredReports.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={columns.length} align="center">
                                        No hay informes registrados
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredReports
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map(row => (
                                        <TableRow hover role="checkbox" tabIndex={-1} key={row.id}>
                                            <TableCell>{row.tipo}</TableCell>
                                            <TableCell>{row.profesional}</TableCell>
                                            <TableCell align="right">{row.paciente}</TableCell>
                                            <TableCell align="right">{row.fecha}</TableCell>
                                            <TableCell align="right">
                                                <IconButton
                                                    color="primary"
                                                    onClick={() => { navigate(`/ver-cuestionarios/${row.idTipo}/${row.pacienteId}`); }}
                                                >
                                                    <VisibilityIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[10, 25, 100]}
                    component="div"
                    count={filteredReports.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    onRowsPerPageChange={e => { setRowsPerPage(+e.target.value); setPage(0); }}
                />
            </Paper>
        </>
    );
}

export default OtrosTable;
